import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Download, Layers, Pencil, RefreshCw, Search } from 'lucide-react';

import PageContainer from '../components/PageContainer';
import { PayrollRangePicker, PayrollRange, defaultPayrollRange } from '../components/payroll/PayrollRangePicker';
import { AdjustShift, ChangeShiftTimes, PAGE_SIZE_OPTIONS, PayrollShiftTable, PayrollTotalsGrid, ShiftTypeLines, sumTotals, totalsOf } from '../components/payroll/PayrollShiftTable';
import RateHistoryDialog from '../components/payroll/RateHistoryDialog';
import ShiftTypesDialog from '../components/payroll/ShiftTypesDialog';
import { RatesInRange } from '../components/payroll/RatesInRange';
import { Button, EmptyState, Input, LoadingState, LocalPagination, ResponsiveDataView, SectionPanel, StatusBadge, useLocalPagination, useToast } from '../components/ui';
import { attendanceApi, payrollApi } from '../services/api';
import type { PayrollPerson, PayrollShift, PayrollSummary, PersonPayroll } from '../types';
import { formatDateKey, formatVnd, formatWeekday } from '../utils/money';
import { reportLink } from '../utils/reportLink';
import { openOnRowClick } from '../utils/rowClick';
import { matchesQuery } from '../utils/search';

const roleLabels: Record<string, string> = {
    ADMIN: 'Quản trị viên',
    MANAGER: 'Quản lý',
    TEACHER: 'Giáo viên',
    ASSISTANT: 'Trợ giảng',
    STAFF: 'Nhân viên',
};

function personName(person: PayrollPerson) {
    return `${person.firstName} ${person.lastName}`.trim() || person.email;
}

function personMatches(person: PayrollPerson, query: string) {
    return matchesQuery(`${personName(person)} ${person.email} ${roleLabels[person.role] ?? ''}`, query);
}

// What a shift can be found by: date, class, campus, role, shift type, note and issues.
function shiftSearchText(shift: PayrollShift) {
    return [
        formatDateKey(shift.date), formatWeekday(shift.date), shift.className, shift.campusName,
        shift.slot === 'TEACHER' ? 'Giảng dạy' : 'Trợ giảng', shift.shiftTypeName, shift.adminNote,
        ...(shift.issues ?? []).flatMap(issue => [issue.label, issue.detail]),
    ].filter(Boolean).join(' ');
}

function SearchBox({ value, onChange, placeholder, label }: { value: string; onChange: (value: string) => void; placeholder: string; label: string }) {
    return (
        <Input
            aria-label={label}
            placeholder={placeholder}
            leftIcon={<Search className="h-4 w-4" />}
            value={value}
            onChange={event => onChange(event.target.value)}
            className="sm:w-72"
        />
    );
}

function rangeLabel(range: PayrollRange) {
    return `${formatDateKey(range.from)} – ${formatDateKey(range.to)}`;
}

function PersonDetail({ userId, range, onBack }: { userId: string; range: PayrollRange; onBack: () => void }) {
    const { showToast } = useToast();
    const navigate = useNavigate();
    const [payroll, setPayroll] = useState<PersonPayroll | null>(null);
    const [loading, setLoading] = useState(true);
    const [editingRates, setEditingRates] = useState(false);
    const [query, setQuery] = useState('');

    const load = useCallback(async () => {
        try {
            const response = await payrollApi.getPerson(userId, range);
            setPayroll(response.data.data);
        } catch (error: any) {
            showToast(error?.message || 'Không tải được bảng công.', 'error');
        } finally {
            setLoading(false);
        }
    }, [userId, range, showToast]);

    useEffect(() => {
        setLoading(true);
        load();
    }, [load]);

    const adjust: AdjustShift = async (shift, patch) => {
        try {
            await payrollApi.adjustShift(shift.attendanceId, shift.slot, patch);
            await load();
        } catch (error: any) {
            showToast(error?.message || 'Lưu thất bại.', 'error');
            throw error;
        }
    };

    const changeTimes: ChangeShiftTimes = async (shift, times) => {
        try {
            await attendanceApi.updateStaffTimes(shift.attendanceId, { slot: shift.slot === 'TEACHER' ? 'teacher' : 'assistant', ...times });
            showToast('Đã lưu giờ. Tab điểm danh cũng đã cập nhật.', 'success');
            await load();
        } catch (error: any) {
            showToast(error?.message || 'Lưu giờ thất bại.', 'error');
            throw error;
        }
    };

    if (loading && !payroll) return <LoadingState title="Đang tải bảng công" />;
    if (!payroll) return <EmptyState title="Không tải được bảng công" description="Thử tải lại trang." />;
    const shifts = query ? payroll.shifts.filter(shift => matchesQuery(shiftSearchText(shift), query)) : payroll.shifts;
    const totals = query ? totalsOf(shifts) : payroll.totals;

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
                <Button variant="ghost" size="sm" icon={<ArrowLeft className="h-4 w-4" />} onClick={onBack}>Tất cả nhân sự</Button>
                <Button
                    variant="secondary"
                    size="sm"
                    className="ml-auto"
                    icon={<Download className="h-4 w-4" />}
                    onClick={() => navigate(reportLink('payroll-person', { ...range, staffId: userId }))}
                >
                    Xuất bảng chấm công
                </Button>
            </div>
            <SectionPanel className="space-y-4 p-4 sm:p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                        <h2 className="text-lg font-semibold text-ink-900">{personName(payroll.user)}</h2>
                        <p className="text-sm text-ink-500">
                            {roleLabels[payroll.user.role] ?? payroll.user.role} · {payroll.user.email} · {rangeLabel(payroll)}
                        </p>
                    </div>
                    {!payroll.user.isActive ? <StatusBadge tone="warning">Ngưng hoạt động</StatusBadge> : null}
                </div>
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                        <RatesInRange rates={payroll.rates} from={payroll.from} />
                    </div>
                    <Button variant="secondary" size="sm" icon={<Pencil className="h-4 w-4" />} onClick={() => setEditingRates(true)}>
                        Lịch sử lương
                    </Button>
                </div>
                <p className="text-xs text-ink-500">
                    Tiền ca = mức lương của loại ca của lớp (ví dụ Ca 120 phút) vào ngày dạy, tính đủ ca kể cả khi đi trễ hoặc về sớm.
                </p>
            </SectionPanel>
            <div className="flex flex-wrap items-center gap-3">
                <SearchBox label="Tìm ca" placeholder="Tìm theo lớp, cơ sở, ngày, ghi chú…" value={query} onChange={setQuery} />
                {query ? <p className="text-sm text-ink-500">{shifts.length}/{payroll.shifts.length} ca · tổng tính theo kết quả tìm</p> : null}
            </div>
            <PayrollTotalsGrid totals={totals} />
            {query && !shifts.length ? (
                <EmptyState title="Không có ca nào khớp" description="Thử tìm bằng từ khác." />
            ) : (
                <PayrollShiftTable
                    shifts={shifts}
                    editable
                    onAdjust={adjust}
                    onChangeTimes={changeTimes}
                    linkIssues
                />
            )}
            <RateHistoryDialog
                person={editingRates ? { id: payroll.user.id, fullName: personName(payroll.user) } : null}
                onClose={() => {
                    setEditingRates(false);
                    load();
                }}
            />
        </div>
    );
}

export const PEOPLE_PAGE_SIZE = 20;

// rows: the people matching the search.
function SummaryTable({ summary: all, rows, onOpen }: { summary: PayrollSummary; rows: PayrollSummary['rows']; onOpen: (userId: string) => void }) {
    const { page, pageSize, pagedItems, setPage, setPageSize } = useLocalPagination(rows, PEOPLE_PAGE_SIZE, rows.map(row => row.user.id).join(','));
    if (all.rows.length === 0) {
        return <EmptyState title="Chưa có ca dạy" description="Không ai đăng ký ca dạy/trợ giảng trong khoảng thời gian này." />;
    }
    if (rows.length === 0) return <EmptyState title="Không có ai khớp" description="Thử tìm bằng tên hoặc email khác." />;
    const summary = { ...all, rows: pagedItems };
    const header = 'px-4 py-3 text-left text-xs font-medium text-ink-500';
    return (
        <>
        <ResponsiveDataView
            desktopLabel="Tổng hợp chấm công"
            mobileLabel="Tổng hợp chấm công"
            desktop={(
                <table className="min-w-full divide-y divide-ink-200 text-sm">
                    <thead>
                        <tr>
                            <th className={header}>Nhân sự</th>
                            <th className={`${header} text-right`}>Ca dạy</th>
                            <th className={`${header} text-right`}>Ca trợ giảng</th>
                            <th className={header}>Số ca theo loại</th>
                            <th className={`${header} text-right`}>Tiền ca</th>
                            <th className={`${header} text-right`}>Phạt</th>
                            <th className={`${header} text-right`}>Thưởng</th>
                            <th className={`${header} text-right`}>Thực nhận</th>
                            <th className={header}><span className="sr-only">Mở</span></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-ink-100 bg-white">
                        {summary.rows.map(({ user, totals }) => (
                            <tr key={user.id} className="cursor-pointer align-top hover:bg-ink-100/60" onClick={openOnRowClick(() => onOpen(user.id))}>
                                <td className="px-4 py-3">
                                    <button type="button" className="text-left font-medium text-academy-800 hover:underline" onClick={() => onOpen(user.id)}>
                                        {personName(user)}
                                    </button>
                                    <p className="text-xs text-ink-500">{roleLabels[user.role] ?? user.role}</p>
                                    {totals.missingRateShifts ? (
                                        <p className="text-xs text-warning-700">{totals.missingRateShifts} ca chưa có mức lương</p>
                                    ) : null}
                                </td>
                                <td className="px-4 py-3 text-right tabular-nums">{totals.teachingShifts}</td>
                                <td className="px-4 py-3 text-right tabular-nums">{totals.assistantShifts}</td>
                                <td className="px-4 py-3 text-xs text-ink-700"><ShiftTypeLines totals={totals} /></td>
                                <td className="px-4 py-3 text-right tabular-nums">{formatVnd(totals.amount)}</td>
                                <td className="px-4 py-3 text-right tabular-nums">{formatVnd(totals.penalty)}</td>
                                <td className="px-4 py-3 text-right tabular-nums">{formatVnd(totals.bonus)}</td>
                                <td className="px-4 py-3 text-right font-semibold tabular-nums">{formatVnd(totals.total)}</td>
                                <td className="px-2 py-3 text-ink-400"><ChevronRight className="h-4 w-4" /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            mobile={(
                <>
                    {summary.rows.map(({ user, totals }) => (
                        <button
                            key={user.id}
                            type="button"
                            onClick={() => onOpen(user.id)}
                            className="w-full rounded-lg border border-ink-200 bg-white p-4 text-left hover:bg-ink-50"
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="font-semibold text-ink-900">{personName(user)}</p>
                                    <p className="text-xs text-ink-500">
                                        {totals.teachingShifts} ca dạy · {totals.assistantShifts} ca trợ giảng
                                    </p>
                                    <ShiftTypeLines totals={totals} className="mt-1 text-xs text-ink-500" />
                                    {totals.missingRateShifts ? (
                                        <p className="text-xs text-warning-700">{totals.missingRateShifts} ca chưa có mức lương</p>
                                    ) : null}
                                </div>
                                <div className="flex items-center gap-1">
                                    <span className="font-semibold tabular-nums">{formatVnd(totals.total)}</span>
                                    <ChevronRight className="h-4 w-4 text-ink-400" />
                                </div>
                            </div>
                        </button>
                    ))}
                </>
            )}
        />
        <LocalPagination
            label="nhân sự"
            page={page}
            pageSize={pageSize}
            totalItems={rows.length}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
        />
        </>
    );
}

export default function Payroll() {
    const { showToast } = useToast();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const selectedUserId = searchParams.get('userId');
    const [range, setRange] = useState<PayrollRange>(() => {
        const from = searchParams.get('from');
        const to = searchParams.get('to');
        return from && to ? { from, to } : defaultPayrollRange();
    });
    const [summary, setSummary] = useState<PayrollSummary | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshKey, setRefreshKey] = useState(0);
    const [managingTypes, setManagingTypes] = useState(false);
    const [personQuery, setPersonQuery] = useState('');

    const changeRange = (next: PayrollRange) => {
        setRange(next);
        const params = new URLSearchParams(searchParams);
        params.set('from', next.from);
        params.set('to', next.to);
        setSearchParams(params, { replace: true });
    };

    const openPerson = (userId: string | null) => {
        const params = new URLSearchParams(searchParams);
        if (userId) params.set('userId', userId);
        else params.delete('userId');
        setSearchParams(params);
    };

    useEffect(() => {
        if (selectedUserId) return;
        let active = true;
        setLoading(true);
        payrollApi.getSummary(range)
            .then(response => active && setSummary(response.data.data))
            .catch((error: any) => active && showToast(error?.message || 'Không tải được bảng chấm công.', 'error'))
            .finally(() => active && setLoading(false));
        return () => {
            active = false;
        };
    }, [range, selectedUserId, refreshKey, showToast]);

    const matchingRows = summary ? summary.rows.filter(({ user }) => personMatches(user, personQuery)) : [];

    return (
        <PageContainer
            title="Chấm công"
            subtitle="Ca dạy và trợ giảng lấy từ đăng ký buổi học."
            actions={(
                <div className="flex flex-wrap gap-2">
                    {!selectedUserId ? (
                        <Button variant="secondary" icon={<Download className="h-4 w-4" />} onClick={() => navigate(reportLink('payroll-summary', range))}>
                            Xuất Excel
                        </Button>
                    ) : null}
                    <Button variant="secondary" icon={<Layers className="h-4 w-4" />} onClick={() => setManagingTypes(true)}>
                        Loại ca
                    </Button>
                    <Button variant="secondary" icon={<RefreshCw className="h-4 w-4" />} onClick={() => setRefreshKey(key => key + 1)}>
                        Làm mới
                    </Button>
                </div>
            )}
        >
            <PayrollRangePicker value={range} onChange={changeRange} />
            {selectedUserId ? (
                <PersonDetail key={`${selectedUserId}-${refreshKey}`} userId={selectedUserId} range={range} onBack={() => openPerson(null)} />
            ) : loading || !summary ? (
                <LoadingState title="Đang tải bảng chấm công" />
            ) : (
                <>
                    <SearchBox label="Tìm nhân sự" placeholder="Tìm theo tên, email…" value={personQuery} onChange={setPersonQuery} />
                    {matchingRows.length ? <PayrollTotalsGrid totals={sumTotals(matchingRows.map(row => row.totals))} people={matchingRows.length} /> : null}
                    <SectionPanel className="overflow-hidden p-0 max-lg:rounded-none max-lg:border-0 max-lg:bg-transparent max-lg:shadow-none">
                        <SummaryTable summary={summary} rows={matchingRows} onOpen={openPerson} />
                    </SectionPanel>
                </>
            )}
            <ShiftTypesDialog
                open={managingTypes}
                onClose={() => {
                    setManagingTypes(false);
                    setRefreshKey(key => key + 1);
                }}
            />
        </PageContainer>
    );
}
